import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, Typography, Avatar, Button, Skeleton, Chip } from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'Services/APIService';

interface Group {
  group_id: string;
  label: string;
  description?: string;
  avatar?: string;
  peopleCount?: number;
}

const AVATAR_COLORS = ['#E8F5E9', '#FFF8E1', '#F1F8E9', '#E3F2FD', '#F3E5F5', '#FBE9E7', '#E0F2F1'];

const PartnerCarousel: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    axiosInstance
      .get('/public/get-groups-list')
      .then((res) => {
        const data = res.data?.groups ?? res.data ?? [];
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading || isPaused || groups.length === 0) return;

    const element = scrollRef.current;
    if (!element) return;

    const intervalId = window.setInterval(() => {
      const maxScrollLeft = element.scrollWidth - element.clientWidth;
      if (maxScrollLeft <= 0) return;

      const atEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - 2;
      if (atEnd) {
        element.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      element.scrollBy({ left: 220, behavior: 'smooth' });
    }, 2500);

    return () => window.clearInterval(intervalId);
  }, [loading, isPaused, groups.length]);

  const handleJoin = (groupId: string) => {
    navigate(`/login?tab=join-community&groupId=${encodeURIComponent(groupId)}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, '&::-webkit-scrollbar': { display: 'none' } }}>
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} variant="rounded" width={200} height={190} sx={{ borderRadius: 4, flexShrink: 0 }} />
        ))}
      </Box>
    );
  }

  if (!groups.length) return null;

  return (
    <Box
      ref={scrollRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        pb: 2,
        scrollBehavior: 'smooth',
        '&::-webkit-scrollbar': { display: 'none' }
      }}
    >
      {groups.map((group, i) => {
        const initials = group.label
          ?.split(' ')
          .map((w) => w[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
        const bgColor = AVATAR_COLORS[i % AVATAR_COLORS.length];

        return (
          <Card
            key={group.group_id}
            elevation={0}
            sx={{
              minWidth: 200,
              maxWidth: 220,
              flexShrink: 0,
              p: 2.5,
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: '0 4px 20px rgba(10,127,116,0.12)', borderColor: 'rgba(10,127,116,0.3)' }
            }}
          >
            <Avatar
              src={group.avatar || undefined}
              sx={{ bgcolor: bgColor, color: '#333', fontWeight: 700, width: 56, height: 56 }}
            >
              {!group.avatar && initials}
            </Avatar>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {group.label}
            </Typography>

            {group.description && (
              <Typography
                variant="caption"
                sx={{
                  color: '#888',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.4
                }}
              >
                {group.description}
              </Typography>
            )}

            {group.peopleCount !== undefined && (
              <Chip
                icon={<PeopleIcon sx={{ fontSize: '12px !important' }} />}
                label={`${group.peopleCount} members`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#f5f5f5' }}
              />
            )}

            <Button
              variant="contained"
              size="small"
              onClick={() => handleJoin(group.group_id)}
              sx={{
                mt: 'auto',
                width: '100%',
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                bgcolor: '#0A7F74',
                '&:hover': { bgcolor: '#04534B' }
              }}
            >
              Join
            </Button>
          </Card>
        );
      })}
    </Box>
  );
};

export default PartnerCarousel;